// Page de configuration de l'Agent IA
// Personnalité, autonomie, questions de qualification

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, Save, Settings, MessageSquare, Shield, 
  Clock, MapPin, Zap, AlertTriangle, Plus, X,
  Volume2, Target
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const TONES = [
  { value: 'formal', label: 'Formel', description: 'Vouvoiement, ton professionnel' },
  { value: 'professional', label: 'Professionnel', description: 'Équilibré et courtois' },
  { value: 'friendly', label: 'Amical', description: 'Chaleureux et accessible' },
  { value: 'casual', label: 'Décontracté', description: 'Tutoiement, ton léger' }
];

const AUTONOMY_LEVELS = [
  { 
    value: 'info_only', 
    label: 'Information uniquement',
    description: 'L\'agent présente et répond aux questions'
  },
  { 
    value: 'with_quotes', 
    label: 'Avec estimations',
    description: 'Peut donner des fourchettes de prix'
  },
  { 
    value: 'full_autonomy', 
    label: 'Autonomie totale',
    description: 'Qualifie et peut planifier des RDV'
  },
  { 
    value: 'supervised', 
    label: 'Supervisé',
    description: 'Chaque réponse doit être validée'
  }
];

export default function VendorSettingsPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    agent_name: '',
    tone: 'professional',
    custom_greeting: '',
    autonomy_level: 'with_quotes',
    require_approval: false,
    can_give_quotes: true,
    can_schedule_appointments: false,
    can_qualify_leads: true,
    qualification_questions: [],
    qualification_criteria: '',
    business_hours: '',
    response_delay_info: '',
    service_area: [],
    custom_instructions: '',
    forbidden_topics: []
  });
  
  const [newQuestion, setNewQuestion] = useState('');
  const [newArea, setNewArea] = useState('');
  const [newForbidden, setNewForbidden] = useState('');

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setFormData({
          agent_name: data.agent_name || '',
          tone: data.tone || 'professional',
          custom_greeting: data.custom_greeting || '',
          autonomy_level: data.autonomy_level || 'with_quotes',
          require_approval: data.require_approval || false,
          can_give_quotes: data.can_give_quotes !== false,
          can_schedule_appointments: data.can_schedule_appointments || false,
          can_qualify_leads: data.can_qualify_leads !== false,
          qualification_questions: data.qualification_questions || [],
          qualification_criteria: data.qualification_criteria || '',
          business_hours: data.business_hours || '',
          response_delay_info: data.response_delay_info || '',
          service_area: data.service_area || [],
          custom_instructions: data.custom_instructions || '',
          forbidden_topics: data.forbidden_topics || []
        });
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const res = await fetch(`${API_URL}/api/vendor/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setConfig(updated);
        toast.success('Configuration enregistrée');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    if (newQuestion.trim()) {
      setFormData({
        ...formData,
        qualification_questions: [...formData.qualification_questions, newQuestion.trim()]
      });
      setNewQuestion('');
    }
  };

  const removeQuestion = (idx) => {
    setFormData({
      ...formData,
      qualification_questions: formData.qualification_questions.filter((_, i) => i !== idx)
    });
  };

  const addArea = () => {
    if (newArea.trim()) {
      setFormData({
        ...formData,
        service_area: [...formData.service_area, newArea.trim()]
      });
      setNewArea('');
    }
  };

  const removeArea = (idx) => {
    setFormData({
      ...formData,
      service_area: formData.service_area.filter((_, i) => i !== idx)
    });
  };

  const addForbidden = () => {
    if (newForbidden.trim()) {
      setFormData({
        ...formData,
        forbidden_topics: [...formData.forbidden_topics, newForbidden.trim()]
      });
      setNewForbidden('');
    }
  };

  const removeForbidden = (idx) => {
    setFormData({
      ...formData,
      forbidden_topics: formData.forbidden_topics.filter((_, i) => i !== idx)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="vendor-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="text-blue-600" size={28} />
            Configuration de l'Agent
          </h1>
          <p className="text-gray-600 mt-1">
            Personnalise le comportement de ton assistant commercial
          </p>
        </div>
        
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <>
              <Save size={18} className="mr-2" />
              Enregistrer
            </>
          )}
        </Button>
      </div>

      {/* Personality Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot size={20} />
            Personnalité
          </CardTitle>
          <CardDescription>
            Définis comment ton agent se présente aux visiteurs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'agent
              </label>
              <Input
                value={formData.agent_name}
                onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                placeholder="Ex: Sophie, Max, Assistant"
              />
              <p className="text-xs text-gray-500 mt-1">
                Le nom qui apparaîtra dans le chat
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ton de communication
              </label>
              <div className="grid grid-cols-2 gap-2">
                {TONES.map((tone) => (
                  <button
                    key={tone.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, tone: tone.value })}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      formData.tone === tone.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-sm">{tone.label}</p>
                    <p className="text-xs text-gray-500">{tone.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message d'accueil personnalisé
            </label>
            <textarea
              value={formData.custom_greeting}
              onChange={(e) => setFormData({ ...formData, custom_greeting: e.target.value })}
              placeholder="Ex: Bonjour ! Je suis Sophie, ton assistante. Comment puis-je t'aider aujourd'hui ?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Autonomy Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={20} />
            Niveau d'autonomie
          </CardTitle>
          <CardDescription>
            Jusqu'où ton agent peut aller sans intervention
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {AUTONOMY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => setFormData({ ...formData, autonomy_level: level.value })}
                className={`p-4 rounded-xl border-2 text-left transition-colors ${
                  formData.autonomy_level === level.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="font-semibold">{level.label}</p>
                <p className="text-sm text-gray-600 mt-1">{level.description}</p>
              </button>
            ))}
          </div>

          <div className="border-t pt-6 space-y-4">
            <h4 className="font-medium text-gray-900">Capacités spécifiques</h4>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.can_give_quotes}
                  onChange={(e) => setFormData({ ...formData, can_give_quotes: e.target.checked })}
                  className="rounded"
                />
                <span>Peut donner des estimations de prix</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.can_qualify_leads}
                  onChange={(e) => setFormData({ ...formData, can_qualify_leads: e.target.checked })}
                  className="rounded"
                />
                <span>Peut qualifier les prospects</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.can_schedule_appointments}
                  onChange={(e) => setFormData({ ...formData, can_schedule_appointments: e.target.checked })}
                  className="rounded"
                />
                <span>Peut planifier des rendez-vous</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.require_approval}
                  onChange={(e) => setFormData({ ...formData, require_approval: e.target.checked })}
                  className="rounded"
                />
                <span className="flex items-center gap-2">
                  <Shield size={16} className="text-orange-500" />
                  Validation requise avant envoi des réponses
                </span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Qualification Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target size={20} />
            Qualification des Leads
          </CardTitle>
          <CardDescription>
            Questions pour mieux qualifier tes prospects
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Questions de qualification
            </label>
            <div className="space-y-2">
              {formData.qualification_questions.map((q, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm">{q}</span>
                  <button
                    onClick={() => removeQuestion(idx)}
                    className="p-1.5 hover:bg-red-100 rounded"
                  >
                    <X size={16} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Ex: Quel est ton budget approximatif ?"
                onKeyPress={(e) => e.key === 'Enter' && addQuestion()}
              />
              <Button type="button" variant="outline" onClick={addQuestion}>
                <Plus size={18} />
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Critères de qualification (pour l'IA)
            </label>
            <textarea
              value={formData.qualification_criteria}
              onChange={(e) => setFormData({ ...formData, qualification_criteria: e.target.value })}
              placeholder="Ex: Un bon lead doit avoir un budget > 500€, un besoin dans les 3 mois, et une localisation dans ma zone d'intervention."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </CardContent>
      </Card>

      {/* Business Info Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock size={20} />
            Informations Business
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horaires d'ouverture
              </label>
              <Input
                value={formData.business_hours}
                onChange={(e) => setFormData({ ...formData, business_hours: e.target.value })}
                placeholder="Ex: Lun-Ven 8h-18h, Sam 9h-12h"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Délai de réponse
              </label>
              <Input
                value={formData.response_delay_info}
                onChange={(e) => setFormData({ ...formData, response_delay_info: e.target.value })}
                placeholder="Ex: Réponse sous 24h"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MapPin size={16} />
              Zone d'intervention
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.service_area.map((area, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  {area}
                  <button onClick={() => removeArea(idx)} className="hover:text-blue-900">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                placeholder="Ex: Paris, 92, Île-de-France"
                onKeyPress={(e) => e.key === 'Enter' && addArea()}
              />
              <Button type="button" variant="outline" onClick={addArea}>
                <Plus size={18} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare size={20} />
            Instructions avancées
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructions personnalisées
            </label>
            <textarea
              value={formData.custom_instructions}
              onChange={(e) => setFormData({ ...formData, custom_instructions: e.target.value })}
              placeholder="Instructions supplémentaires pour l'IA. Ex: Toujours mentionner la garantie 2 ans, proposer un devis gratuit..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              Sujets à éviter
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.forbidden_topics.map((topic, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                  {topic}
                  <button onClick={() => removeForbidden(idx)} className="hover:text-red-900">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newForbidden}
                onChange={(e) => setNewForbidden(e.target.value)}
                placeholder="Ex: Concurrents, politique de prix..."
                onKeyPress={(e) => e.key === 'Enter' && addForbidden()}
              />
              <Button type="button" variant="outline" onClick={addForbidden}>
                <Plus size={18} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button (bottom) */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          size="lg"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <>
              <Save size={18} className="mr-2" />
              Enregistrer la configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
